import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  parseDate,
  formatDate,
  Course,
  InputWithConfirm,
  RadioOptionsWithConfirm,
  CardContainer,
  CardWithModal,
  getCourseScholarships,
  calculateFeeAfterScholarships,
  calculateScholarshipsSum,
} from 'shared-components';
import { Button } from '@chakra-ui/react';

import { ApplicationState } from '../../../../store';
import { savePI, bulkUpdateScholarshipSponsor } from '../../../../store/student/actions';
import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';
import { createUpdatePIFieldAction } from '../../../../store/student/personalInfo/actions';
import { createUpdateOfferFieldAction } from '../../../../store/student/offer/actions';
import { Pages } from '../../../../store/student/personalInfo/types';
import { InputFieldConfig, ICourse, IImportCourse, IConflictHistory, ScholarshipSponsorship } from '../../../../types';
import { formatCurrency } from '../../../../utilities/formatting';
import { getPageStatus, Visible, Mandatory } from './utils';
import { compareCourses } from '../../../../utilities/pages';
import { getImportOffer } from '../../../../utilities/helpers';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';
import { IUserRole } from 'types';
import { useSession } from 'shared-components/authentication';

/* convert imported course to student form
   this has to return a partial course,
   because some fields may still not be filled out in import course
   (in this case, students have to edit the form and set these fields themselves)
 */
function importCourseToStudent(
  importCourse: IImportCourse,
  index: number,
  conflictHistory: IConflictHistory,
): Partial<ICourse> {
  const conflictOfferEvidence = conflictHistory?.courseOfferEvidence?.find(x => x.index === index)?.data;
  return {
    courseName: importCourse.title,
    courseLevel: importCourse.level,
    startDate: importCourse.startDate,
    endDate: importCourse.endDate,
    courseFee: importCourse.courseFeeFirstYear ?? undefined,
    cah3: importCourse.cah3,
    isWorkPlacement: importCourse.isWorkPlacement,
    isAtas: importCourse.isATASRequired,
    innerLondon: importCourse.innerLondon,
    lastAcceptanceDate: importCourse.lastAcceptanceDate,
    // CS-2162 in phase 1 of adding offerEvidence,
    // there isn't a separate page to edit offerEvidence field.
    // To make this field visible on data view, it is silently added here.

    // Prioritize resolved conflict value.
    // This is because this field may have a resolved conflict, before it was ever edited by a student
    // (when admin edits offer evidence text).
    offerEvidence: conflictOfferEvidence ? conflictOfferEvidence.resolvedValue : importCourse.offerEvidence,
  };
}

const PersonalQPage5 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onNext = (config: any) => {
    dispatch(savePI(Pages.PAGE5, config));
  };

  const onChange = useCallback(
    (fieldName: string, value: any, refValue?: any) => {
      if (fieldName === Q.COURSES) {
        dispatch(createUpdateOfferFieldAction(fieldName, value, refValue));
      } else {
        dispatch(createUpdatePIFieldAction(fieldName, value, refValue));
      }
    },
    [dispatch],
  );

  const { navigateToStudentDashboard } = useCustomNavigate();
  const onBackToTaskList = () => navigateToStudentDashboard();
  const onUpdateScholarship = (data: { key: string; data: ScholarshipSponsorship }[]) =>
    dispatch(bulkUpdateScholarshipSponsor(data));

  // state props from store
  const student = useSelector(({ student }: ApplicationState) => student, shallowEqual);

  const importData = useSelector(({ student }: ApplicationState) => student.importData, shallowEqual);

  const conflictHistory = useSelector(({ student }: ApplicationState) => student.conflictHistory, shallowEqual);

  const finance = useSelector(({ student }: ApplicationState) => student.financials);

  const isPageComplete = useSelector(({ student }: ApplicationState) => student.offer.isPIPage5Complete, shallowEqual);

  const tuitionPostDiscount = student.config.tuitionPostDiscount ?? false;

  const {
    StudentNumber: studentNumberConfig,
    CASReceived: casReceivedConfig,
  }: {
    StudentNumber: InputFieldConfig;
    CASReceived: InputFieldConfig;
  } = student.config.Checklist.PersonalInfo.Page5;

  const { studentNo = studentNumberConfig?.defaultValue as string | undefined } = student.personalInfo;

  const { casReceived = casReceivedConfig?.defaultValue as string | undefined } = student.offer;

  // constants
  const currentStep = 5;
  const steps = 10;

  // component state
  const [complete, setComplete] = useState(false);
  const [visible, setVisible] = useState<Visible>();
  const [mandatory, setMandatory] = useState<Mandatory>();
  const [studentNumber] = useState(studentNo);
  const [showModal, setShowModal] = useState<boolean[]>([]);

  const [importApproved, setImportApproved] = useState<string[]>([]);
  const [importUpdateEnabled, setImportUpdateEnabled] = useState<string[]>([]);
  const [importEdited, setImportEdited] = useState<string[]>([]);
  const [studentNumberVisible, setStudentNumberVisible] = useState(false);
  const [casReceivedVisible, setCasReceivedVisible] = useState(false);
  const [importDataCourses, setImportDataCourses] = useState<IImportCourse[]>([]);
  const [importRemoved, setImportRemoved] = useState<string[]>([]);
  const [restorePressed, setRestorePressed] = useState(false);

  const courses = useMemo<ICourse[]>(() => {
    const piCourses = student.offer.courses ?? [];
    // Pre-fill each course with a 'null' object.
    // This is needed so we can override each index with '{}' empty object
    // when a course is removed.
    // User is required to approve/remove every course, so by they time
    // page is submitted, no 'null' objects are left in this array.
    importDataCourses.forEach((_importCourse, i) => {
      if (!piCourses[i]) {
        // @ts-ignore
        piCourses[i] = null;
      }
    });
    return piCourses;
  }, [student.offer.courses, importDataCourses]);

  const importOffer = getImportOffer(importData, student.offer.intakeId, student.offer.offerId);

  useEffect(() => {
    setImportApproved(getImportApprovedFields());
    setImportUpdateEnabled(getImportUpdateFields());
    setImportRemoved(getImportRemovedFields());
  }, [importData, importDataCourses, isPageComplete]);

  useEffect(() => {
    if ((importOffer?.courses?.length ?? 0) > 0) {
      setImportDataCourses(importOffer!.courses);
    } else {
      setImportDataCourses([
        {
          title: '',
          startDate: '',
          endDate: '',
          courseFeeFirstYear: undefined,
          isWorkPlacement: undefined,
        },
      ]);
    }
  }, [importOffer]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    const { studentNumber: isStudentNumberVisible, casReceived: isCasReceivedVisible } = visible;

    setStudentNumberVisible(isStudentNumberVisible);
    setCasReceivedVisible(isCasReceivedVisible);
  }, [visible]);

  useEffect(() => {
    const {
      complete: pageComplete,
      visible: pageVisible,
      mandatory: pageMandatory,
    } = getPageStatus(
      student.config,
      {
        studentNo,
        courses,
        casReceived: casReceived === 'Yes',
      },
      true,
    );

    setComplete(pageComplete);
    setVisible(pageVisible);
    setMandatory(pageMandatory);
  }, [studentNo, courses, casReceived]);

  const handleCasReceivedClick = (question: any) => {
    onChange(Q.CAS_RECEIVED, question.label);
  };

  const getImportApprovedFields = (): Array<string> => {
    const importApproved = [];
    if (isPageComplete) {
      if (importData.studentNo === studentNo) {
        importApproved.push(Q.STUDENT_NUMBER);
      }
      if (importDataCourses.length > 0) {
        importDataCourses.forEach((importCourse: IImportCourse, index: number) => {
          if ((courses[index] && courses[index].courseName !== '') || compareCourses(importCourse, courses[index])) {
            importApproved.push(`${Q.COURSES}-${index + 1}`);
          }
        });
      }
    }
    return importApproved;
  };
  const getImportUpdateFields = (): Array<string> => {
    const importUpdateEnabled = [];
    if (isPageComplete) {
      if (importData.studentNo && studentNo !== importData.studentNo) {
        importUpdateEnabled.push(Q.STUDENT_NUMBER);
      }
    }
    return importUpdateEnabled;
  };

  const getImportRemovedFields = (): Array<string> => {
    const importRemovedFields: string[] = [];
    if (isPageComplete) {
      if (importDataCourses.length > 0) {
        importDataCourses.forEach((importCourse: IImportCourse, index: number) => {
          if (
            (!courses[index] || courses[index].courseName === '' || courses[index].courseName === undefined) &&
            !compareCourses(importCourse, courses[index])
          ) {
            importRemovedFields.push(`${Q.COURSES}-${index + 1}`);
          }
        });
      }
    }
    return importRemovedFields;
  };

  const isImportEnabled = (target: string): boolean => {
    if (target === Q.STUDENT_NUMBER) {
      return studentNumberVisible && !!importData.studentNo;
    }
    if (target.includes(Q.COURSES)) {
      return importDataCourses.length > 0;
    }
    return false;
  };

  const isImportUpdateEnabled = (target: string): boolean => {
    if (!isPageComplete) return false;
    if (target === Q.STUDENT_NUMBER) {
      return !!studentNo && importUpdateEnabled.includes(target);
    }
    if (target.includes(Q.COURSES)) {
      return importUpdateEnabled.includes(target);
    }

    return false;
  };

  const handleImportEdit = (target: string): void => {
    setImportApproved(importApproved.filter(q => q !== target));
    setImportEdited([...importEdited, target]);
  };

  const handleImportUndo = (target: string): void => {
    setImportApproved(importApproved.filter(q => q !== target));
    setImportEdited(importEdited.filter(q => q !== target));
  };

  const handleImportApprove = (target: string): void => {
    setImportApproved([...importApproved, target]);
    setImportEdited(importEdited.filter(q => q !== target));
  };

  const isImportComplete = (target: string): boolean => {
    if (!isImportEnabled(target) || isImportUpdateEnabled(target) || importRemoved.find(q => q === target)) {
      return true;
    }
    return importApproved.includes(target) || (!target.includes(`${Q.COURSES}`) && importEdited.includes(target));
  };

  const getComplete = (): boolean => {
    const importQuestions: string[] = [Q.STUDENT_NUMBER];
    importDataCourses.forEach((_, index: number) => {
      importQuestions.push(`${Q.COURSES}-${index + 1}`);
    });
    const importComplete = importQuestions.every(q => isImportComplete(q));
    return complete && importComplete;
  };

  const toggleModal = (index: number) => {
    const newShowModal = [...showModal];
    newShowModal[index] = !showModal[index];
    setShowModal(newShowModal);
  };

  const getCardItems = (course: Partial<ICourse>) => {
    const cardItems = [];
    const courseScholarships = getCourseScholarships(
      course.courseName,
      course.courseFee,
      finance.scholarshipSponsorship,
    );

    if (visible?.startDate) {
      cardItems.push({
        label: 'Start Date',
        value: course.startDate ? formatDate(parseDate(course.startDate), 'DD MMM YYYY') : '',
        required: true,
      });
    }
    if (visible?.endDate) {
      cardItems.push({
        label: 'Exp. End Date',
        value: course.endDate ? formatDate(parseDate(course.endDate), 'DD MMM YYYY') : '',
        required: true,
      });
    }
    if (visible?.courseFee) {
      cardItems.push({
        label: '1st Year Course Fee',
        value: course.courseFee !== undefined ? `£${formatCurrency(course.courseFee!)}` : '',
        required: true,
      });
    }
    if (Object.keys(courseScholarships).length !== 0 && calculateScholarshipsSum(courseScholarships) > 0) {
      cardItems.push({
        label: 'Scholarships Applied',
        value: `£${formatCurrency(calculateScholarshipsSum(courseScholarships))}`,
        required: true,
      });

      cardItems.push({
        label: 'Fee After Scholarship(s)',
        value: `£${formatCurrency(calculateFeeAfterScholarships(course.courseFee, courseScholarships))}`,
        required: true,
        textColor: '#008765',
      });
    }
    if (visible?.isWorkPlacement) {
      cardItems.push({
        label: 'Work Placement',
        value: course.isWorkPlacement !== undefined ? (course.isWorkPlacement ? 'Yes' : 'No') : '',
        required: true,
      });
    }
    return cardItems;
  };

  const { groups } = useSession();
  const itemsDisabled = {
    autoCourseName: false,
    casReceived: false,
    courseFee: false,
    endDate: false,
    isWorkPlacement: false,
    manualCourseName:
      groups.includes(IUserRole.AgencyAdmin) || groups.includes(IUserRole.Agent) || groups.includes(IUserRole.Student),
    startDate: false,
    studentNumber: false,
  };

  const getCourse = (course: Partial<ICourse>, index: number) => (
    // @ts-ignore
    <Course
      index={index}
      level={course.courseLevel}
      levelOther={course.courseLevelOther}
      startDate={course.startDate}
      endDate={course.endDate}
      cah3={course.cah3}
      isWorkPlacement={course.isWorkPlacement}
      isAtas={course.isAtas}
      lastAcceptanceDate={course.lastAcceptanceDate}
      innerLondon={course.innerLondon}
      courseName={course.courseName}
      courseFee={course.courseFee !== undefined ? course.courseFee : undefined}
      visible={visible as any}
      mandatory={mandatory!}
      itemsDisabled={itemsDisabled}
      config={student.config as any}
      isScholarshipDisabled={tuitionPostDiscount}
      courseScholarships={getCourseScholarships(course.courseName, course.courseFee, finance.scholarshipSponsorship)}
      onUpdateScholarship={onUpdateScholarship}
      suggestions={[]}
      apiCallsInProgress={[]}
      getCourseSuggestions={() => null}
      clearSuggestions={() => null}
      onSaveAndClose={(updCourse: Partial<ICourse>) => {
        const newCourses = [...courses];
        newCourses[index] = {
          ...course,
          ...(updCourse as ICourse),
        };
        handleImportEdit(`${Q.COURSES}-${index + 1}`);
        onChange(Q.COURSES, newCourses);
        toggleModal(index);
      }}
    />
  );

  const getCoursesForCard = () => {
    let hasRemovedCourses = false;
    const courseCount = courses.filter(
      course => course && course?.courseName !== '' && course?.courseName !== undefined,
    ).length;
    let coursesRemoved = 0;

    importDataCourses.forEach((_importCourse: IImportCourse, index: number) => {
      const key = `${Q.COURSES}-${index + 1}`;
      if (
        (isPageComplete &&
          courseCount > 0 &&
          (!courses[index] || courses[index].courseName === undefined || courses[index].courseName === '') &&
          !restorePressed) ||
        importRemoved.find(q => q === key)
      ) {
        hasRemovedCourses = true;
        coursesRemoved += 1;
      }
    });

    const cards = importDataCourses.map((importCourse: IImportCourse, index: number) => {
      const key = `${Q.COURSES}-${index + 1}`;
      const course = courses[index] || importCourseToStudent(importCourse, index, conflictHistory);

      if (
        (isPageComplete &&
          courseCount > 0 &&
          (!courses[index] || courses[index].courseName === undefined || courses[index].courseName === '') &&
          !restorePressed) ||
        importRemoved.find(q => q === key)
      ) {
        return null;
      }

      const missingCourses = importRemoved && importRemoved.length ? importRemoved.length : coursesRemoved;

      return (
        <CardWithModal
          key={index}
          id={`course-${index}`}
          index={index}
          modalTitle={'Edit a course'}
          hasInCardActions={false}
          hasRemoveCardAction={
            !student.config.personalInfoPage5Uneditable &&
            (courseCount > 1 ||
              ((courseCount === 0 || courseCount === 1) &&
                importDataCourses.length > 1 &&
                missingCourses + 1 < importDataCourses.length))
          }
          cardTitleRequired={true}
          cardTitle={course.courseName ?? ''}
          cardItems={getCardItems(course)}
          hasAddMoreButton={false}
          showModal={showModal[index]}
          toggleModal={toggleModal}
          importEnabled={isImportEnabled(`${Q.COURSES}-${index + 1}`)}
          importUpdateEnabled={false}
          importApproved={importApproved.includes(`${Q.COURSES}-${index + 1}`)}
          importEdited={importEdited.includes(`${Q.COURSES}-${index + 1}`)}
          onImportApprove={() => {
            if (
              (courses[index] && courses[index].courseName !== '') ||
              importEdited.includes(`${Q.COURSES}-${index + 1}`)
            ) {
              handleImportApprove(`${Q.COURSES}-${index + 1}`);
            } else {
              const newCourses = [...courses];
              newCourses[index] = importCourseToStudent(importCourse, index, conflictHistory) as ICourse;
              onChange(Q.COURSES, newCourses);
              handleImportApprove(`${Q.COURSES}-${index + 1}`);
            }
          }}
          canUndo={
            isImportEnabled(`${Q.COURSES}-${index + 1}`) ? importEdited.includes(`${Q.COURSES}-${index + 1}`) : false
          }
          onImportUndo={() => {
            handleImportUndo(`${Q.COURSES}-${index + 1}`);
            const newCourses = [...courses];
            newCourses[index] = importCourseToStudent(importCourse, index, conflictHistory) as ICourse;
            onChange(Q.COURSES, newCourses);
          }}
          onRemove={(index: number) => {
            if (student.config.personalInfoPage5Uneditable) return; // Hack to make course info uneditable for STM-287

            const key = `${Q.COURSES}-${index + 1}`;
            setImportRemoved([...importRemoved, key]);
            setImportApproved(importApproved.filter(q => q !== key));
            setImportEdited(importEdited.filter(q => q !== key));
            const newCourses = [...courses.slice(0, index), {}, ...courses.slice(index + 1)];
            onChange(Q.COURSES, newCourses);
          }}
          hideEditButton={!!student.config.personalInfoPage5Uneditable}
        >
          {getCourse(course, index)}
        </CardWithModal>
      );
    });
    return (
      <>
        {cards}

        {hasRemovedCourses ? (
          <Button
            id="restore-import-data-button"
            key={`restore-button`}
            data-selector="restore-import-data-button"
            onClick={() => {
              setRestorePressed(true);
              setImportRemoved([]);
              setImportApproved([...importApproved.filter(q => !q.includes(`${Q.COURSES}`))]);
              setImportEdited([...importEdited.filter(q => !q.includes(`${Q.COURSES}`))]);
              onChange(Q.COURSES, [
                ...courses.map((course: any, index: number) =>
                  course?.courseName
                    ? course
                    : (importCourseToStudent(importDataCourses[index], index, conflictHistory) as ICourse),
                ),
              ]);
            }}
          >
            Undo Course Remove
          </Button>
        ) : null}
      </>
    );
  };

  return (
    <QuestionPage
      complete={getComplete()}
      currentStep={currentStep}
      steps={steps}
      onNext={() => onNext(student.config)}
      onBackToTaskList={onBackToTaskList}
      section={'Personal Information'}
    >
      {studentNumberVisible && (
        <InputWithConfirm
          key={Q.STUDENT_NUMBER}
          id={Q.STUDENT_NUMBER}
          value={studentNo}
          label={studentNumberConfig?.label}
          subText={studentNumberConfig?.subLabel}
          inputHelperText={studentNumberConfig?.helpText}
          onChange={(evt: any) => onChange(Q.STUDENT_NUMBER, evt.currentTarget.value)}
          importEnabled={isImportEnabled(Q.STUDENT_NUMBER)}
          importValue={importData.studentNo}
          importUpdateEnabled={isImportUpdateEnabled(Q.STUDENT_NUMBER)}
          importApproved={importApproved.includes(Q.STUDENT_NUMBER)}
          importEdited={importEdited.includes(Q.STUDENT_NUMBER)}
          onImportApprove={() => {
            handleImportApprove(Q.STUDENT_NUMBER);
            onChange(Q.STUDENT_NUMBER, importData.studentNo);
          }}
          onImportEdit={() => {
            if (student.config.personalInfoPage5Uneditable) return; // Hack to make student number uneditable for STM-287
            handleImportEdit(Q.STUDENT_NUMBER);
            onChange(Q.STUDENT_NUMBER, isImportUpdateEnabled(Q.STUDENT_NUMBER) ? studentNo : importData.studentNo);
          }}
          onImportUndo={() => {
            handleImportUndo(Q.STUDENT_NUMBER);
            onChange(Q.STUDENT_NUMBER, isImportUpdateEnabled(Q.STUDENT_NUMBER) ? studentNumber : '');
          }}
          hideEditButton={!!student.config.personalInfoPage5Uneditable}
        />
      )}

      <CardContainer title={'Your Courses'}>{getCoursesForCard()}</CardContainer>

      {casReceivedVisible && (
        <RadioOptionsWithConfirm
          key={Q.CAS_RECEIVED}
          id={Q.CAS_RECEIVED}
          value={casReceived}
          label={casReceivedConfig?.label}
          inputHelperText={casReceivedConfig?.helpText}
          onClick={(question: any) => handleCasReceivedClick(question)}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: casReceived === 'Yes',
            },
            {
              char: 'N',
              label: 'No',
              selected: casReceived === 'No',
            },
          ]}
        />
      )}
    </QuestionPage>
  );
};
export default PersonalQPage5;
